// Sdp.jsx — Halaman ManageEngine ServiceDesk Plus Cloud (Ticketing + Asset).
// Sumber data (read-only, ber-permission 'sdp'):
//   GET /api/sdp/requests → { requests:[{display_id,subject,status,requester_name,requester_email,technician,site,created_time,due_by_time}] }
//   GET /api/sdp/assets   → { assets:[{name,product_type,state,site,category,created_time}] }
// Fetch/tulis data dilakukan lewat cron / Run Now di Table Maintenance (tab SDP Cloud).
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid, LineChart, Line, LabelList,
} from "recharts";
import * as XLSX from "xlsx";
import { RefreshCw, Ticket, HardDrive, CheckCircle2, MapPin, X, AlertTriangle, Clock, BarChart3 } from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";
import CardList from "../components/mobile/CardList";
import DetailSheet from "../components/mobile/DetailSheet";
import TicketDetailDrawer from "../components/TicketDetailDrawer";
import AssetDetailDrawer from "../components/AssetDetailDrawer";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import useTabDeepLink from "../hooks/useTabDeepLink";

// Tab yang boleh datang dari `?tab=` (QuickSearch).
const SDP_TABS = ["requests", "assets", "analytics"];

const MC = ["rgb(var(--brand-600))", "#d97706", "#16a34a", "#7c3aed", "#dc2626", "#0891b2", "#db2777", "#65a30d", "#9333ea", "#0ea5e9"];

const S = {
  page:    { padding: "clamp(12px,4vw,20px) clamp(12px,4vw,24px)", fontFamily: "'Segoe UI',Arial,sans-serif", color: "#1e293b" },
  hdr:     { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title:   { fontSize: "clamp(17px,4vw,20px)", fontWeight: 800, color: "#0f172a", margin: 0 },
  sub:     { fontSize: 13, color: "#64748b", marginTop: 4 },
  card:    { background: "#fff", border: "1px solid #e8edf5", borderRadius: 16, padding: "16px 18px", boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 6px 20px rgba(15,23,42,.05)" },
  cardT:   { fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 14, marginBottom: 18 },
  grid2:   { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16, marginBottom: 16 },
  btn:     { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#475569", fontSize: 13, padding: "7px 12px", cursor: "pointer", fontFamily: "inherit" },
  input:   { padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff" },
  th:      { padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: .5, background: "#f8fafc", color: "#64748b", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" },
  td:      { padding: "8px 12px", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9" },
  tabBar:  { display: "inline-flex", gap: 4, marginBottom: 18, padding: 4, background: "#f1f5f9", borderRadius: 12, flexWrap: "wrap" },
  tab:     { padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#64748b", cursor: "pointer", border: "none", background: "transparent", borderRadius: 9, fontFamily: "inherit", transition: "all .15s" },
  tabOn:   { color: "rgb(var(--brand-700))", background: "#fff", boxShadow: "0 1px 3px rgba(15,23,42,.12)" },
};
const TT = { contentStyle: { background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#94a3b8" }, itemStyle: { color: "#f1f5f9" } };

const API_BASE = window.__API_BASE__ || "";
const getToken = () => localStorage.getItem("auth_token") || localStorage.getItem("token") || "";
const authGet = async (path) => {
  const r = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
};

function KpiCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <div
      style={{ ...S.card, position: "relative", overflow: "hidden", cursor: onClick ? "pointer" : "default", transition: "box-shadow .2s, transform .2s",
               background: `linear-gradient(135deg, #fff 55%, color-mix(in srgb, ${color} 9%, #fff))` }}
      onClick={onClick}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = "0 10px 28px rgba(15,23,42,.12)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
      onMouseLeave={e => { if (onClick) { e.currentTarget.style.boxShadow = S.card.boxShadow; e.currentTarget.style.transform = "none"; } }}
      title={onClick ? "Klik untuk lihat detail" : undefined}
    >
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: color }} />
      <div style={{ position: "absolute", right: -18, bottom: -18, opacity: .07 }}><Icon size={88} color={color} /></div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: color, boxShadow: `0 6px 14px color-mix(in srgb, ${color} 35%, transparent)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={20} color="#fff" />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: .6 }}>{label}</div>
          <div style={{ fontSize: "clamp(20px,4vw,26px)", fontWeight: 800, color: "#0f172a", lineHeight: 1.15, marginTop: 2 }}>{Number(value || 0).toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}

// Hitung distribusi (count per nilai kolom), urut desc, batasi topN.
function distribution(rows, field, topN = 8) {
  const m = new Map();
  rows.forEach(r => { const k = r[field] || "—"; m.set(k, (m.get(k) || 0) + 1); });
  const arr = [...m.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  if (arr.length <= topN) return arr;
  const top = arr.slice(0, topN);
  const rest = arr.slice(topN).reduce((a, x) => a + x.value, 0);
  return [...top, { name: "Lainnya", value: rest }];
}

// Parse string tanggal tampilan SDP ("Sep 8, 2025 11:45 AM") ke epoch ms. NaN bila gagal.
const parseDT = (s) => { if (!s) return NaN; const t = Date.parse(s); return isNaN(t) ? NaN : t; };

// ── Tab Analytics: SLA compliance, kategori masalah, beban teknisi, tren, waktu penyelesaian.
function AnalyticsView({ requests, assets, id, onGoToRequests, onGoToAssets }) {
  const {
    t
  } = useLang();

  const overdueTotal = useMemo(() => requests.filter(r => r.is_overdue).length, [requests]);
  const overduePct = requests.length ? Math.round((overdueTotal / requests.length) * 1000) / 10 : 0;
  const escalatedTotal = useMemo(() => requests.filter(r => r.is_escalated).length, [requests]);

  const overdueByPriority = useMemo(() => {
    const m = new Map();
    requests.forEach(r => {
      const p = r.priority || "—";
      if (!m.has(p)) m.set(p, { priority: p, total: 0, overdue: 0 });
      const o = m.get(p); o.total++; if (r.is_overdue) o.overdue++;
    });
    return [...m.values()]
      .map(x => ({ ...x, pct: x.total ? Math.round((x.overdue / x.total) * 1000) / 10 : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [requests]);

  const categorized = useMemo(() => requests.filter(r => r.category && r.category !== "—"), [requests]);
  const categoryDist = useMemo(() => distribution(categorized, "category", 10), [categorized]);
  const uncategorizedCount = requests.length - categorized.length;

  // Semua teknisi (tanpa batas) — bisa disortir via header tabel.
  const technicianWorkload = useMemo(() => {
    const m = new Map();
    requests.forEach(r => {
      const t = r.technician;
      if (!t) return;
      if (!m.has(t)) m.set(t, { technician: t, total: 0, overdue: 0 });
      const o = m.get(t); o.total++; if (r.is_overdue) o.overdue++;
    });
    return [...m.values()].map(x => ({ ...x, pct: x.total ? Math.round((x.overdue / x.total) * 1000) / 10 : 0 }));
  }, [requests]);

  const [techSort, setTechSort] = useState({ key: "total", dir: -1 });
  const toggleTechSort = (key) => setTechSort(s =>
    s.key !== key ? { key, dir: key === "technician" ? 1 : -1 } : { key, dir: s.dir === 1 ? -1 : s.dir === -1 ? 0 : 1 });
  const technicianSorted = useMemo(() => {
    if (!techSort.key || techSort.dir === 0) return [...technicianWorkload].sort((a, b) => b.total - a.total);
    return [...technicianWorkload].sort((a, b) => {
      const va = techSort.key === "technician" ? a.technician.toLowerCase() : a[techSort.key];
      const vb = techSort.key === "technician" ? b.technician.toLowerCase() : b[techSort.key];
      if (va < vb) return -1 * techSort.dir;
      if (va > vb) return 1 * techSort.dir;
      return 0;
    });
  }, [technicianWorkload, techSort]);

  const monthlyTrend = useMemo(() => {
    const m = new Map();
    requests.forEach(r => {
      const t = parseDT(r.created_time);
      if (isNaN(t)) return;
      const d = new Date(t);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      m.set(key, (m.get(key) || 0) + 1);
    });
    return [...m.entries()].map(([month, count]) => ({ month, count })).sort((a, b) => a.month.localeCompare(b.month));
  }, [requests]);

  const resolutionStats = useMemo(() => {
    const perPriority = new Map();
    let sumDays = 0, cnt = 0;
    requests.forEach(r => {
      const done = r.completed_time || r.resolved_time;
      const c = parseDT(r.created_time), e = parseDT(done);
      if (isNaN(c) || isNaN(e) || e < c) return;
      const days = (e - c) / 86400000;
      sumDays += days; cnt++;
      const p = r.priority || "—";
      if (!perPriority.has(p)) perPriority.set(p, { priority: p, totalDays: 0, count: 0 });
      const o = perPriority.get(p); o.totalDays += days; o.count++;
    });
    const byPriority = [...perPriority.values()]
      .map(x => ({ priority: x.priority, avgDays: x.count ? Math.round((x.totalDays / x.count) * 10) / 10 : 0 }))
      .sort((a, b) => b.avgDays - a.avgDays);
    return { avgOverall: cnt ? Math.round((sumDays / cnt) * 10) / 10 : 0, byPriority, sampleCount: cnt };
  }, [requests]);

  // ── Asset analytics ──────────────────────────────────────────────────────
  const regionDist = useMemo(() => distribution(assets, "region", 10), [assets]);
  const manufacturerDist = useMemo(
    () => distribution(assets.filter(a => a.manufacturer && a.manufacturer !== "-"), "manufacturer", 10),
    [assets]);
  const manufacturerUnknown = assets.length - assets.filter(a => a.manufacturer && a.manufacturer !== "-").length;

  const auditHealth = useMemo(() => {
    const success = assets.filter(a => a.last_audit_status === "SUCCESS").length;
    const failed  = assets.filter(a => a.last_audit_status === "FAILED").length;
    const none    = assets.length - success - failed;
    return { success, failed, none, total: assets.length };
  }, [assets]);

  // Aset per Company, ditumpuk per Product Type (top 9 tipe + Lainnya).
  const companyByType = useMemo(() => {
    const v = (x) => (x == null || x === "" || x === "-" ? "—" : String(x));
    const typeTot = new Map();
    assets.forEach(a => typeTot.set(v(a.product_type), (typeTot.get(v(a.product_type)) || 0) + 1));
    const topTypes = [...typeTot.entries()].sort((a, b) => b[1] - a[1]).slice(0, 9).map(x => x[0]);
    const hasOther = typeTot.size > topTypes.length;
    const types = hasOther ? [...topTypes, "Lainnya"] : topTypes;
    const m = new Map();
    assets.forEach(a => {
      const c = v(a.site), t0 = v(a.product_type), t = topTypes.includes(t0) ? t0 : "Lainnya";
      if (!m.has(c)) m.set(c, { company: c, total: 0 });
      const o = m.get(c); o[t] = (o[t] || 0) + 1; o.total++;
    });
    return { types, rows: [...m.values()].sort((a, b) => b.total - a.total) };
  }, [assets]);
  const shortCo = (s) => String(s).replace(/^PT\.?\s*/i, "").replace(/Industri(es)?$/i, "").trim();

  const failedAssets = useMemo(
    () => assets.filter(a => a.last_audit_status === "FAILED")
      .sort((a, b) => (parseDT(b.last_audit_time) || 0) - (parseDT(a.last_audit_time) || 0)),
    [assets]);

  const dataCompleteness = useMemo(() => {
    const total = assets.length || 1;
    const pct = (n) => Math.round((n / total) * 1000) / 10;
    return {
      serial: pct(assets.filter(a => a.serial_number).length),
      region: pct(assets.filter(a => a.region).length),
      audited: pct(assets.filter(a => a.last_audit_status).length),
      barcode: pct(assets.filter(a => a.barcode).length),
    };
  }, [assets]);

  const thTech = (key, label) => {
    const active = techSort.key === key && techSort.dir !== 0;
    const arrow = !active ? "↕" : techSort.dir === 1 ? "↑" : "↓";
    return (
      <th key={key} style={{ ...S.th, cursor: "pointer", userSelect: "none", color: active ? "rgb(var(--brand-600))" : S.th.color }}
          onClick={() => toggleTechSort(key)} title={t('sdp.clickSort')}>
        {label} <span style={{ opacity: active ? 1 : .4, fontSize: 11 }}>{arrow}</span>
      </th>
    );
  };

  return (
    <div>
      <div style={S.kpiGrid}>
        <KpiCard icon={AlertTriangle} label={t('sdp.overdueTickets')} value={overdueTotal} color="#dc2626"
          onClick={() => onGoToRequests(r => !!r.is_overdue, id ? "Overdue" : "Overdue")} />
        <KpiCard icon={AlertTriangle} label={id ? "% Overdue" : "% Overdue"}          value={overduePct}    color="#dc2626"
          onClick={() => onGoToRequests(r => !!r.is_overdue, id ? "Overdue" : "Overdue")} />
        <KpiCard icon={AlertTriangle} label={t('sdp.escalated')}        value={escalatedTotal} color="#d97706"
          onClick={() => onGoToRequests(r => !!r.is_escalated, t('sdp.escalated'))} />
        <KpiCard icon={Clock}         label={t('sdp.avgResolutionDays')} value={resolutionStats.avgOverall} color="#0891b2" />
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.slaOverduePriority')}</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={overdueByPriority}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="priority" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" />
              <Tooltip {...TT} formatter={(v, n, p) => [`${v}% (${p.payload.overdue}/${p.payload.total})`, id ? "Overdue" : "Overdue"]} />
              <Bar dataKey="pct" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.issueCategoriesTop10')}</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={categoryDist} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip {...TT} />
              <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
            {id ? `${uncategorizedCount.toLocaleString()} tiket belum dikategorikan (tidak ditampilkan).` : `${uncategorizedCount.toLocaleString()} tickets uncategorized (not shown).`}
          </div>
        </div>
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.monthlyTicketVolumeTrend')}</div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip {...TT} />
              <Line type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.avgResolutionTimePriorityDays')}</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={resolutionStats.byPriority}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="priority" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip {...TT} />
              <Bar dataKey="avgDays" fill="#0891b2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
            {id ? `Berdasarkan ${resolutionStats.sampleCount.toLocaleString()} tiket yang punya completed/resolved time.` : `Based on ${resolutionStats.sampleCount.toLocaleString()} tickets with completed/resolved time.`}
          </div>
        </div>
      </div>
      <div style={S.card}>
        <div style={S.cardT}>{t('sdp.technicianWorkloadLength', {
          length: technicianSorted.length
        })}</div>
        <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {thTech("technician", t('sdp.technician'))}
              {thTech("total", t('sdp.totalTickets'))}
              {thTech("overdue", "Overdue")}
              {thTech("pct", "% Overdue")}
            </tr></thead>
            <tbody>
              {technicianSorted.length === 0
                ? <tr><td style={S.td} colSpan={4}>—</td></tr>
                : technicianSorted.map((t, i) => (
                  <tr key={i}>
                    <td style={S.td}>{t.technician}</td>
                    <td style={S.td}>{t.total}</td>
                    <td style={S.td}>{t.overdue}</td>
                    <td style={{ ...S.td, color: t.pct > 30 ? "#dc2626" : t.pct > 10 ? "#d97706" : "#16a34a", fontWeight: 700 }}>{t.pct}%</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ ...S.cardT, marginTop: 26, marginBottom: -2, fontSize: 12, color: "#475569" }}>
        {t('sdp.assetAnalytics')}
      </div>
      <div style={S.kpiGrid}>
        <KpiCard icon={CheckCircle2}  label={t('sdp.auditSuccess')} value={auditHealth.success} color="#16a34a"
          onClick={() => onGoToAssets(a => a.last_audit_status === "SUCCESS", t('sdp.auditSuccess'))} />
        <KpiCard icon={AlertTriangle} label={t('sdp.auditFailed')}   value={auditHealth.failed}  color="#dc2626"
          onClick={() => onGoToAssets(a => a.last_audit_status === "FAILED", t('sdp.auditFailed'))} />
        <KpiCard icon={Clock}         label={t('sdp.notAudited')}  value={auditHealth.none}    color="#94a3b8"
          onClick={() => onGoToAssets(a => !a.last_audit_status, t('sdp.notAudited'))} />
        <KpiCard icon={BarChart3}     label={t('sdp.serialNumber')} value={dataCompleteness.serial} color="#0891b2" />
      </div>
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ ...S.cardT, justifyContent: "space-between" }}>
          <span>Assets per Company · per Product Type</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "rgb(var(--brand-700))", background: "rgb(var(--brand-50))", padding: "3px 10px", borderRadius: 999, letterSpacing: 0, textTransform: "none" }}>
            {assets.length.toLocaleString()} assets · {companyByType.rows.length} companies
          </span>
        </div>
        <ResponsiveContainer width="100%" height={Math.max(260, companyByType.rows.length * 44 + 60)}>
          <BarChart data={companyByType.rows} layout="vertical" margin={{ left: 10, right: 50 }} barCategoryGap={8}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="company" width={190}
                   tickFormatter={(c) => { const r = companyByType.rows.find(x => x.company === c); return `${shortCo(c)}  (${r ? r.total.toLocaleString() : 0})`; }}
                   tick={{ fontSize: 12, fill: "#334155" }} axisLine={false} tickLine={false} />
            <Tooltip {...TT} cursor={{ fill: "rgba(148,163,184,.12)" }}
                     formatter={(v, n, p) => [`${v.toLocaleString()} (${Math.round(v / (p.payload.total || 1) * 100)}%)`, n]} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {companyByType.types.map((ty, i) => (
              <Bar key={ty} dataKey={ty} stackId="a" fill={MC[i % MC.length]}
                   style={{ cursor: "pointer" }}
                   onClick={(d) => d && onGoToAssets(a => (a.site || "—") === d.company &&
                     (ty === "Lainnya" ? !companyByType.types.includes(a.product_type || "—") : (a.product_type || "—") === ty),
                     `Company: ${d.company} · Type: ${ty}`)} />
            ))}

          </BarChart>
        </ResponsiveContainer>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>Klik segmen untuk membuka daftar aset company + product type tersebut. Tipe di luar 9 terbesar digabung jadi "Lainnya".</div>
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.assetsRegion')}</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={regionDist} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip {...TT} />
              <Bar dataKey="value" fill="#0891b2" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.assetsManufacturerTop10')}</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={manufacturerDist} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip {...TT} />
              <Bar dataKey="value" fill="#7c3aed" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
            {id ? `${manufacturerUnknown.toLocaleString()} aset tanpa manufacturer (tidak ditampilkan).` : `${manufacturerUnknown.toLocaleString()} assets without manufacturer (not shown).`}
          </div>
        </div>
      </div>
      <div style={S.card}>
        <div style={S.cardT}>{t('sdp.failedAuditAssetsLength', {
          length: failedAssets.length
        })}</div>
        <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={S.th}>{t('sdp.name')}</th>
              <th style={S.th}>{id ? "Region" : "Region"}</th>
              <th style={S.th}>{id ? "Site" : "Site"}</th>
              <th style={S.th}>{t('sdp.description')}</th>
              <th style={S.th}>{t('sdp.auditTime')}</th>
            </tr></thead>
            <tbody>
              {failedAssets.length === 0
                ? <tr><td style={S.td} colSpan={5}>{t('sdp.none')}</td></tr>
                : failedAssets.map((a, i) => (
                  <tr key={i}>
                    <td style={S.td}>{a.name}</td>
                    <td style={S.td}>{a.region || "—"}</td>
                    <td style={S.td}>{a.site || "—"}</td>
                    <td style={{ ...S.td, color: "#dc2626" }}>{a.last_audit_description || "—"}</td>
                    <td style={S.td}>{a.last_audit_time || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
          {t('sdp.dataCompletenessSerialHaveSerial', {
            serial: dataCompleteness.serial,
            region: dataCompleteness.region,
            audited: dataCompleteness.audited,
            barcode: dataCompleteness.barcode
          })}
        </div>
      </div>
    </div>
  );
}

// ── Pivot aset: baris × kolom (Company / Product Type / State / Region / Category) → jumlah aset.
const PIVOT_DIMS = [
  { k: "site", h: "Company" }, { k: "product_type", h: "Product Type" }, { k: "state", h: "State" },
  { k: "region", h: "Region" }, { k: "category", h: "Category" }, { k: "manufacturer", h: "Manufacturer" },
];
const REQ_PIVOT_DIMS = [
  { k: "technician", h: "Technician" }, { k: "status", h: "Status" }, { k: "site", h: "Company" },
  { k: "priority", h: "Priority" }, { k: "category", h: "Category" }, { k: "requester_name", h: "Requester" },
  { k: "__month", h: "Month (Created)" },
];
function AssetPivot({ assets, onPick, dims = PIVOT_DIMS, init = { row: "site", row2: "product_type", col: "state" }, title = "Pivot Assets", note }) {
  const PIVOT_DIMS = dims; // eslint-disable-line no-shadow
  const [open, setOpen] = useState(true);
  const [rowK, setRowK] = useState(init.row);
  const [colK, setColK] = useState(init.col);
  const [row2K, setRow2K] = useState(init.row2); // "" = tanpa sub-rows
  const [rowSort, setRowSort] = useState("total"); // total | name
  const [collapsed, setCollapsed] = useState({});
  const val = (a, k) => {
    if (k === "__month") {
      const t = parseDT(a.created_time); if (isNaN(t)) return "—";
      const d = new Date(t); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    return a[k] == null || a[k] === "" || a[k] === "-" ? "—" : String(a[k]);
  };
  const SEP = "\u0001";
  const pv = useMemo(() => {
    const cells = new Map(), rowT = new Map(), colT = new Map(), subs = new Map();
    const add = (m, k) => m.set(k, (m.get(k) || 0) + 1);
    assets.forEach(a => {
      const r = val(a, rowK), c = val(a, colK);
      add(cells, r + SEP + c); add(rowT, r); add(colT, c);
      if (row2K) {
        const s = val(a, row2K), rk = r + SEP + s;
        if (!subs.has(r)) subs.set(r, new Map());
        add(subs.get(r), s);
        add(cells, rk + SEP + SEP + c); add(rowT, rk + SEP + SEP);
      }
    });
    const srt = (keys, tot) => keys.sort((a, b) => rowSort === "name" ? a.localeCompare(b) : tot(b) - tot(a));
    const rows = srt([...rowT.keys()].filter(k => !k.includes(SEP)), k => rowT.get(k));
    const subRows = {};
    if (row2K) rows.forEach(r => { const m = subs.get(r) || new Map(); subRows[r] = srt([...m.keys()], k => m.get(k)); });
    const colsAll = [...colT.keys()].sort((a, b) => colT.get(b) - colT.get(a));
    const max = Math.max(1, ...rows.flatMap(r => colsAll.map(c => cells.get(r + SEP + c) || 0)));
    const maxSub = Math.max(1, ...[...cells.entries()].filter(([k]) => k.includes(SEP + SEP)).map(([, v]) => v));
    return { cells, rowT, colT, rows, subRows, cols: colsAll, max, maxSub };
  }, [assets, rowK, row2K, colK, rowSort]); // eslint-disable-line
  const dimLabel = (k) => (PIVOT_DIMS.find(d => d.k === k) || {}).h;
  const pick = (r, c, s) => {
    const pred = a => (r == null || val(a, rowK) === r) && (s == null || val(a, row2K) === s) && (c == null || val(a, colK) === c);
    const label = [r != null && `${dimLabel(rowK)}: ${r}`, s != null && `${dimLabel(row2K)}: ${s}`, c != null && `${dimLabel(colK)}: ${c}`].filter(Boolean).join(" · ");
    onPick(pred, label || "All");
  };
  const allOpen = pv.rows.every(r => !collapsed[r]);
  const exportPivot = () => {
    const data = [];
    pv.rows.forEach(r => {
      const o = { [dimLabel(rowK)]: r, ...(row2K ? { [dimLabel(row2K)]: "(subtotal)" } : {}) };
      pv.cols.forEach(c => { o[c] = pv.cells.get(r + SEP + c) || 0; });
      o.Total = pv.rowT.get(r); data.push(o);
      if (row2K) (pv.subRows[r] || []).forEach(s => {
        const rk = r + SEP + s, so = { [dimLabel(rowK)]: r, [dimLabel(row2K)]: s };
        pv.cols.forEach(c => { so[c] = pv.cells.get(rk + SEP + SEP + c) || 0; });
        so.Total = pv.rowT.get(rk + SEP + SEP); data.push(so);
      });
    });
    const tot = { [dimLabel(rowK)]: "Total" }; pv.cols.forEach(c => { tot[c] = pv.colT.get(c); }); tot.Total = assets.length;
    data.push(tot);
    const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pivot");
    XLSX.writeFile(wb, `sdp_asset_pivot_${rowK}_x_${colK}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const sel = { ...S.input, padding: "5px 8px", fontSize: 12, cursor: "pointer" };
  const cellTh = { ...S.th, position: "sticky", top: 0, zIndex: 3, textAlign: "right" };
  return (
    <div style={{ ...S.card, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: open ? 12 : 0 }}>
        <div style={{ ...S.cardT, marginBottom: 0, cursor: "pointer" }} onClick={() => setOpen(o => !o)}>
          <span style={{ display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span>
          {title}
        </div>
        {open && <>
          <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>Rows</span>
          <select style={sel} value={rowK} onChange={e => { setRowK(e.target.value); if (e.target.value === row2K) setRow2K(""); setCollapsed({}); }}>
            {PIVOT_DIMS.map(d => <option key={d.k} value={d.k} disabled={d.k === colK}>{d.h}</option>)}
          </select>
          <span style={{ fontSize: 12, color: "#64748b" }}>Sub-rows</span>
          <select style={sel} value={row2K} onChange={e => setRow2K(e.target.value)}>
            <option value="">(none)</option>
            {PIVOT_DIMS.map(d => <option key={d.k} value={d.k} disabled={d.k === rowK || d.k === colK}>{d.h}</option>)}
          </select>
          <button style={{ ...S.btn, padding: "5px 9px" }} title="Tukar baris/kolom" onClick={() => { setRowK(colK); setColK(rowK); if (row2K === colK) setRow2K(""); }}>⇄</button>
          <span style={{ fontSize: 12, color: "#64748b" }}>Columns</span>
          <select style={sel} value={colK} onChange={e => setColK(e.target.value)}>
            {PIVOT_DIMS.map(d => <option key={d.k} value={d.k} disabled={d.k === rowK || d.k === row2K}>{d.h}</option>)}
          </select>
          {row2K && <button style={{ ...S.btn, padding: "5px 10px" }}
            onClick={() => setCollapsed(allOpen ? Object.fromEntries(pv.rows.map(r => [r, 1])) : {})}>
            {allOpen ? "⊟ Collapse all" : "⊞ Expand all"}</button>}
          <select style={sel} value={rowSort} onChange={e => setRowSort(e.target.value)}>
            <option value="total">Sort: Total ↓</option><option value="name">Sort: Name A–Z</option>
          </select>
          <button style={{ ...S.btn, marginLeft: "auto", padding: "5px 12px" }} onClick={exportPivot}>⬇️ Export Pivot</button>
        </>}
      </div>
      {open && (
        <div style={{ overflow: "auto", maxHeight: 460, border: "1px solid #eef2f7", borderRadius: 10 }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
            <thead><tr>
              <th style={{ ...S.th, position: "sticky", top: 0, left: 0, zIndex: 5, minWidth: 180 }}>{dimLabel(rowK)} \ {dimLabel(colK)}</th>
              {pv.cols.map(c => <th key={c} style={cellTh}>{c}</th>)}
              <th style={{ ...cellTh, background: "rgb(var(--brand-50))", color: "rgb(var(--brand-700))" }}>Total</th>
            </tr></thead>
            <tbody>
              {pv.rows.flatMap(r => {
                const isOpen = row2K && !collapsed[r];
                const line = (key, label, sub, total, getN, max) => (
                  <tr key={key} className="sdp-tr">
                    <td style={{ ...S.td, position: "sticky", left: 0, zIndex: 2, whiteSpace: "nowrap", cursor: "pointer",
                                 background: sub ? "#fff" : (row2K ? "#f8fafc" : "#fff"), fontWeight: sub ? 500 : 700,
                                 paddingLeft: sub ? 34 : 12, color: sub ? "#475569" : "#0f172a" }}
                        onClick={() => sub ? pick(r, null, label) : (row2K ? setCollapsed(p => ({ ...p, [r]: !p[r] })) : pick(r, null))}>
                      {!sub && row2K && <span style={{ display: "inline-block", width: 14, color: "#94a3b8", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span>}
                      {label}
                      {!sub && row2K && <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 6 }}>({(pv.subRows[r] || []).length})</span>}
                    </td>
                    {pv.cols.map(c => {
                      const n = getN(c);
                      const a = n ? .08 + .55 * (n / max) : 0;
                      return (
                        <td key={c} onClick={() => n && (sub ? pick(r, c, label) : pick(r, c))}
                            style={{ ...S.td, textAlign: "right", cursor: n ? "pointer" : "default", fontVariantNumeric: "tabular-nums",
                                     background: n ? `rgb(var(--brand-600) / ${a.toFixed(2)})` : (!sub && row2K ? "#f8fafc" : "transparent"),
                                     color: n ? (a > .4 ? "#fff" : "#0f172a") : "#cbd5e1", fontWeight: n ? (sub ? 500 : 700) : 400 }}
                            title={n ? `${r}${sub ? " · " + label : ""} · ${c}: ${n} — klik untuk filter` : ""}>
                          {n ? n.toLocaleString() : "·"}
                        </td>
                      );
                    })}
                    <td style={{ ...S.td, textAlign: "right", fontWeight: sub ? 600 : 800, background: "rgb(var(--brand-50))", cursor: "pointer" }}
                        onClick={() => sub ? pick(r, null, label) : pick(r, null)}>{(total || 0).toLocaleString()}</td>
                  </tr>
                );
                const out = [line(r, r, false, pv.rowT.get(r), c => pv.cells.get(r + SEP + c) || 0, pv.max)];
                if (isOpen) (pv.subRows[r] || []).forEach(s => {
                  const rk = r + SEP + s;
                  out.push(line(rk, s, true, pv.rowT.get(rk + SEP + SEP), c => pv.cells.get(rk + SEP + SEP + c) || 0, pv.maxSub));
                });
                return out;
              })}
            </tbody>
            <tfoot><tr>
              <td style={{ ...S.td, position: "sticky", left: 0, bottom: 0, zIndex: 4, background: "rgb(var(--brand-50))", fontWeight: 800 }}>Total</td>
              {pv.cols.map(c => (
                <td key={c} onClick={() => pick(null, c)}
                    style={{ ...S.td, position: "sticky", bottom: 0, zIndex: 3, textAlign: "right", fontWeight: 800, background: "rgb(var(--brand-50))", cursor: "pointer" }}>
                  {pv.colT.get(c).toLocaleString()}
                </td>
              ))}
              <td style={{ ...S.td, position: "sticky", bottom: 0, zIndex: 3, textAlign: "right", fontWeight: 800, background: "rgb(var(--brand-600))", color: "#fff" }}>
                {assets.length.toLocaleString()}
              </td>
            </tr></tfoot>
          </table>
        </div>
      )}
      {open && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>{note || "Klik angka / nama baris / total kolom untuk memfilter tabel Assets di bawah. Mengikuti filter global (Company, Product Type, Asset State)."}</div>}
    </div>
  );
}

export default function Sdp() {
  const {
    lang,
    t
  } = useLang();
  const id = lang !== "en";
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const p = searchParams.get("tab");
    return SDP_TABS.includes(p) ? p : "requests";
  });   // requests | assets | analytics
  useTabDeepLink("sdp", (p) => SDP_TABS.includes(p), setTab);
  const [allRequests, setRequests] = useState([]);
  const [allAssets, setAssets] = useState([]);
  // Filter global (bar di bawah filter tahun): memengaruhi KPI, chart, tabel.
  // Company = site; Ticket Status hanya ke requests; Product Type & Asset State hanya ke assets.
  const [gCompany, setGCompany] = useState("");
  const [gStatus, setGStatus] = useState("");
  const [gProduct, setGProduct] = useState("");
  const [gState, setGState] = useState("");
  const gOpts = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter((v) => v != null && v !== "" && v !== "null"))].sort((a, b) => String(a).localeCompare(String(b)));
    return {
      company: uniq([...allRequests.map((r) => r.site), ...allAssets.map((a) => a.site)]),
      status: uniq(allRequests.map((r) => r.status)),
      product: uniq(allAssets.map((a) => a.product_type)),
      state: uniq(allAssets.map((a) => a.state)),
    };
  }, [allRequests, allAssets]);
  const requests = useMemo(() => allRequests.filter((r) =>
    (!gCompany || r.site === gCompany) && (!gStatus || r.status === gStatus)), [allRequests, gCompany, gStatus]);
  const assets = useMemo(() => allAssets.filter((a) =>
    (!gCompany || a.site === gCompany) && (!gProduct || a.product_type === gProduct) && (!gState || a.state === gState)),
    [allAssets, gCompany, gProduct, gState]);
  const gActive = !!(gCompany || gStatus || gProduct || gState);
  const gReset = () => { setGCompany(""); setGStatus(""); setGProduct(""); setGState(""); };
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 0 }); // dir: 0 none, 1 asc, -1 desc
  const [selectedYear, setSelectedYear] = useState("All");   // Poin 1: filter tahun
  const [quickFilter, setQuickFilter] = useState(null);      // Poin 3: filter dari klik KPI — {label, pred}

  // Reset sort saat pindah tab (kolom antar-tab berbeda).
  useEffect(() => { setSort({ key: null, dir: 0 }); }, [tab]);

  // Daftar tahun yang tersedia dari created_time (dihitung dari data mentah, tak terpengaruh filter aktif).
  const yearsAvailable = useMemo(() => {
    const s = new Set();
    requests.forEach(r => { const t = parseDT(r.created_time); if (!isNaN(t)) s.add(new Date(t).getFullYear()); });
    return [...s].sort((a, b) => a - b);
  }, [requests]);

  // Default: saat data pertama kali termuat, pilih tahun terakhir yang ada.
  const [yearInit, setYearInit] = useState(false);
  useEffect(() => {
    if (!yearInit && yearsAvailable.length) {
      setSelectedYear(yearsAvailable[yearsAvailable.length - 1]);
      setYearInit(true);
    }
  }, [yearsAvailable, yearInit]);

  // Requests setelah filter tahun (dipakai untuk KPI, chart, tab Requests & Analytics).
  const yearRequests = useMemo(() => {
    if (selectedYear === "All") return requests;
    return requests.filter(r => { const t = parseDT(r.created_time); return !isNaN(t) && new Date(t).getFullYear() === selectedYear; });
  }, [requests, selectedYear]);

  // Navigasi dari klik KPI card: pindah tab + (opsional) set filter cepat.
  const goToRequests = useCallback((pred, label) => {
    setQuickFilter(pred ? { label, pred } : null);
    setTab("requests");
  }, []);
  const goToAssets = useCallback((pred, label) => {
    setQuickFilter(pred ? { label, pred } : null);
    setTab("assets");
  }, []);

  // Kolom bertipe tanggal → di-parse ke epoch untuk sort kronologis yang benar.
  const DATE_COLS = new Set(["created_time", "due_by_time"]);
  const toSortVal = (row, key) => {
    const v = row[key];
    if (v == null || v === "") return DATE_COLS.has(key) ? -Infinity : "";
    if (DATE_COLS.has(key)) { const t = Date.parse(v); return isNaN(t) ? -Infinity : t; }
    const n = Number(v);
    return (typeof v === "string" && v.trim() !== "" && !isNaN(n)) ? n : String(v).toLowerCase();
  };
  const toggleSort = (key) => setSort(s =>
    s.key !== key ? { key, dir: 1 } : { key, dir: s.dir === 1 ? -1 : s.dir === -1 ? 0 : 1 });

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [rq, as] = await Promise.all([authGet("/api/sdp/requests"), authGet("/api/sdp/assets")]);
      setRequests(rq.requests || []);
      setAssets(as.assets || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // KPI (berdasarkan yearRequests — sudah kena filter tahun)
  const closedCount = useMemo(
    () => yearRequests.filter(r => /closed|resolved/i.test(r.status || "")).length,
    [yearRequests]);
  const openCount = yearRequests.length - closedCount;
  const siteCount = useMemo(() => new Set(assets.map(a => a.site || "—")).size, [assets]);

  // Charts
  const reqByStatus = useMemo(() => distribution(yearRequests, "status"), [yearRequests]);
  const assetByType = useMemo(() => distribution(assets, "product_type"), [assets]);
  // Stacked per State untuk chart Assets by Type
  const assetTypeState = useMemo(() => {
    const topTypes = assetByType.filter(d => d.name !== "Lainnya").map(d => d.name);
    const stTot = new Map();
    assets.forEach(a => { const s = a.state || "—"; stTot.set(s, (stTot.get(s) || 0) + 1); });
    const states = [...stTot.entries()].sort((a, b) => b[1] - a[1]).map(x => x[0]);
    const m = new Map(assetByType.map(d => [d.name, { name: d.name, value: d.value }]));
    assets.forEach(a => {
      const t0 = a.product_type || "—", t = topTypes.includes(t0) ? t0 : "Lainnya";
      const o = m.get(t); if (!o) return;
      const s = a.state || "—"; o[s] = (o[s] || 0) + 1;
    });
    return { states, rows: assetByType.map(d => m.get(d.name)), topTypes };
  }, [assets, assetByType]);

  // ── Filter per kolom ────────────────────────────────────────────────────────
  // Nilai disimpan sebagai { v, exact }: `exact` diisi oleh UI (dropdown = persis,
  // kotak ketik = mengandung). Sengaja TIDAK memakai daftar opsi kolom di sini —
  // daftar itu baru dibuat setelah `cols` dideklarasikan di bawah, dan memakainya
  // dari useMemo ini akan kena TDZ saat render.
  const [colF, setColF] = useState({});
  useEffect(() => { setColF({}); }, [tab]);
  const colFCount = Object.values(colF).filter(f => f && f.v !== "").length;
  const setColFilter = (k, v, exact) => setColF(p => {
    const n = { ...p };
    if (v === "") delete n[k]; else n[k] = { v, exact: !!exact };
    return n;
  });

  // Filter tabel aktif: dasar (tahun, utk requests) → quickFilter (klik KPI) → filter kolom → search → sort.
  const filtered = useMemo(() => {
    const base = tab === "requests" ? yearRequests : assets;
    const afterQuick = (quickFilter && typeof quickFilter.pred === "function") ? base.filter(quickFilter.pred) : base;
    const colEntries = Object.entries(colF).filter(([, f]) => f && f.v !== "");
    const afterCols = !colEntries.length ? afterQuick : afterQuick.filter(r => colEntries.every(([k, f]) => {
      const cell = String(r[k] ?? "");
      return f.exact ? cell === f.v : cell.toLowerCase().includes(String(f.v).toLowerCase());
    }));
    const term = q.trim().toLowerCase();
    const afterSearch = !term ? afterCols : afterCols.filter(r => Object.values(r).some(v => String(v ?? "").toLowerCase().includes(term)));
    if (!sort.key || sort.dir === 0) {
      // Default tab Requests: tiket terbaru di atas (created_time desc, fallback display_id desc).
      if (tab !== "requests") return afterSearch;
      return [...afterSearch].sort((a, b) => {
        const ta = parseDT(a.created_time), tb = parseDT(b.created_time);
        const va = isNaN(ta) ? -Infinity : ta, vb = isNaN(tb) ? -Infinity : tb;
        if (vb !== va) return vb - va;
        return (Number(b.display_id) || 0) - (Number(a.display_id) || 0);
      });
    }
    const sorted = [...afterSearch].sort((a, b) => {
      const va = toSortVal(a, sort.key), vb = toSortVal(b, sort.key);
      if (va < vb) return -1 * sort.dir;
      if (va > vb) return 1 * sort.dir;
      return 0;
    });
    return sorted;
  }, [tab, q, yearRequests, assets, sort, quickFilter, colF]); // eslint-disable-line

  const exportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab === "requests" ? "Requests" : "Assets");
    XLSX.writeFile(wb, `sdp_${tab}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const reqCols = [
    { k: "display_id", h: "Display ID" }, { k: "subject", h: "Subject" }, { k: "status", h: "Status" },
    { k: "requester_name", h: "Requester" }, { k: "technician", h: "Technician" },
    { k: "site", h: "Site" }, { k: "created_time", h: "Created" }, { k: "due_by_time", h: "Due By" },
  ];
  // Kolom rincian diambil dari atribut detail SDP (GET /assets/{id}) yang sudah
   // disimpan per asset -- tidak ada call tambahan ke SDP saat tabel dirender.
  const assetCols = [
    { k: "name", h: "Name" }, { k: "product_type", h: "Product Type" }, { k: "state", h: "State" },
    { k: "product_name", h: "Model" }, { k: "manufacturer", h: "Manufacturer" },
    { k: "serial_number", h: "Serial No." }, { k: "asset_tag", h: "Asset Tag" },
    { k: "ip_address", h: "IP Address" }, { k: "mac_address", h: "MAC Address" },
    { k: "assigned_user", h: "Assigned To" }, { k: "department", h: "Department" },
    { k: "site", h: "Site" }, { k: "region", h: "Region" }, { k: "location", h: "Location" },
    { k: "category", h: "Category" },
    { k: "acquisition_date", h: "Acquired" }, { k: "warranty_expiry", h: "Warranty Expiry" },
    { k: "created_time", h: "Created" },
    // Spec dari resource /workstations SDP (tabel sdp_workstations, di-join by name).
    // Hanya terisi untuk asset bertipe Workstation.
    { k: "os", h: "OS" }, { k: "os_version", h: "OS Version" },
    { k: "processor", h: "CPU" }, { k: "processor_count", h: "Cores" },
    { k: "ram_gb", h: "RAM (GB)" }, { k: "disk_gb", h: "Disk (GB)" },
    { k: "domain", h: "Domain" }, { k: "last_logged_user", h: "Last Logged User" },
    { k: "service_tag", h: "Service Tag" },
  ];
  const allCols = tab === "requests" ? reqCols : assetCols;

  // ── Pemilih kolom ───────────────────────────────────────────────────────────
  // Default: semua kolom tampil (tidak ada data yang diam-diam disembunyikan).
  // Pilihan disimpan per tab di localStorage, jadi bertahan antar kunjungan.
  const COLS_LS_KEY = `sdp_cols_${tab}`;
  const [hidden, setHidden] = useState({});
  const [colPickerOpen, setColPickerOpen] = useState(false);
  useEffect(() => {
    setColPickerOpen(false);
    try {
      const raw = localStorage.getItem(`sdp_cols_${tab}`);
      setHidden(raw ? JSON.parse(raw) : {});
    } catch (e) { setHidden({}); }
  }, [tab]);
  const toggleCol = (k) => setHidden(prev => {
    const next = { ...prev };
    if (next[k]) delete next[k]; else next[k] = 1;
    try { localStorage.setItem(COLS_LS_KEY, JSON.stringify(next)); } catch (e) {}
    return next;
  });
  // Minimal satu kolom harus tersisa, kalau tidak tabelnya hilang sama sekali.
  const cols = allCols.filter(c => !hidden[c.k]).length ? allCols.filter(c => !hidden[c.k]) : allCols;
  const hiddenCount = allCols.length - cols.length;

  // Kolom dengan sedikit nilai unik dapat dropdown nilai-persis; sisanya kotak ketik.
  const COL_OPT_MAX = 30;
  const colOptions = useMemo(() => {
    const base = tab === "requests" ? yearRequests : assets;
    const out = {};
    for (const c of cols) {
      const set = new Set();
      for (const r of base) {
        const v = r[c.k];
        if (v === null || v === undefined || v === "") continue;
        set.add(String(v));
        if (set.size > COL_OPT_MAX) break;
      }
      if (set.size && set.size <= COL_OPT_MAX) out[c.k] = [...set].sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [tab, yearRequests, assets]); // eslint-disable-line

  // ── Mobile (tabel → kartu) ─────────────────────────────────────────────────
  const isMobile = useIsMobile();
  const [detailRow, setDetailRow] = useState(null); // row utk DetailSheet
  const [ticketRow, setTicketRow] = useState(null); // row utk TicketDetailDrawer (tab requests)
  const [assetRow, setAssetRow] = useState(null); // row utk AssetDetailDrawer (tab assets)
  useEffect(() => { setDetailRow(null); }, [tab]);  // tutup sheet saat ganti tab

  const statusTone = (s = "") =>
    /closed|resolved/i.test(s) ? "emerald" :
    /hold|pending|wait/i.test(s) ? "amber" :
    /dispose|expired|missing|retir/i.test(s) ? "rose" : "blue";

  const cardTitle = (r) => tab === "requests"
    ? (r.subject || `#${r.display_id}`)
    : (r.name || "—");
  const cardBadge = (r) => {
    const s = tab === "requests" ? r.status : r.state;
    return s ? { label: s, tone: statusTone(s) } : null;
  };
  const cardSubtitle = (r) => tab === "requests"
    ? [`#${r.display_id}`, r.requester_name, r.technician].filter(Boolean).join(" · ")
    : [r.product_type, r.site, r.category].filter(Boolean).join(" · ");

  return (
    <div style={S.page}>
      <style>{`
        .sdp-tr:hover td { background: rgb(var(--brand-50)) !important; }
        .sdp-tr:nth-child(even) td { background: #fafbfd; }
        @keyframes sdpSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ ...S.hdr, position: "relative", overflow: "hidden", borderRadius: 18, padding: "20px 24px", color: "#fff",
                    background: "linear-gradient(120deg, rgb(var(--brand-700)) 0%, rgb(var(--brand-600)) 55%, rgb(var(--brand-500)) 100%)",
                    boxShadow: "0 10px 30px rgb(var(--brand-600) / .25)" }}>
        <div style={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.10)" }} />
        <div style={{ position: "absolute", right: 120, bottom: -70, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,.2)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ticket size={26} color="#fff" />
          </div>
          <div>
            <h1 style={{ ...S.title, color: "#fff" }}>ManageEngine SDP Cloud</h1>
            <div style={{ ...S.sub, color: "rgba(255,255,255,.85)" }}>{t('sdp.ticketingAssetsServicedeskPlusCloud')}</div>
          </div>
        </div>
        <button style={{ ...S.btn, position: "relative", background: "rgba(255,255,255,.95)", border: "none", color: "rgb(var(--brand-700))", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,.12)" }} onClick={load} disabled={loading}>
          <RefreshCw size={15} style={loading ? { animation: "sdpSpin 1s linear infinite" } : undefined} /> {loading ? (t('sdp.loading')) : (t('sdp.reload'))}
        </button>
      </div>
      {err && (
        <div style={{ ...S.card, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <X size={16} /> {err}
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#7f1d1d" }}>
            {t('sdp.noDataYetRunTable')}
          </span>
        </div>
      )}
      {/* Poin 1: filter tahun — mempengaruhi KPI, chart, dan tab Requests/Analytics (Assets tidak). */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#64748b", marginRight: 4 }}>{t('sdp.year')}</span>
        {["All", ...yearsAvailable].map(y => (
          <button key={y} onClick={() => setSelectedYear(y)}
            style={{
              ...S.btn, padding: "5px 12px",
              background: selectedYear === y ? "rgb(var(--brand-600))" : "#fff",
              color: selectedYear === y ? "#fff" : "#475569",
              border: selectedYear === y ? "1px solid rgb(var(--brand-600))" : S.btn.border,
            }}>
            {y === "All" ? (t('sdp.all')) : y}
          </button>
        ))}
      </div>
      {/* Filter global: Company / Ticket Status / Product Type / Asset State */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#64748b", marginRight: 4 }}>Filter:</span>
        {[
          ["Company", gCompany, setGCompany, gOpts.company],
          ["Ticket Status", gStatus, setGStatus, gOpts.status],
          ["Product Type", gProduct, setGProduct, gOpts.product],
          ["Asset State", gState, setGState, gOpts.state],
        ].map(([label, val, set, opts]) => (
          <select key={label} value={val} onChange={(e) => set(e.target.value)} title={label}
            style={{ ...S.btn, padding: "5px 10px", maxWidth: 240, cursor: "pointer",
              background: val ? "rgb(var(--brand-50))" : "#fff", color: val ? "rgb(var(--brand-700))" : "#475569",
              border: val ? "1px solid rgb(var(--brand-600))" : S.btn.border, fontWeight: val ? 600 : 400 }}>
            <option value="">{label}: All</option>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        {gActive && (
          <button onClick={gReset} style={{ ...S.btn, padding: "5px 12px", color: "#b91c1c" }}>✕ Reset filters</button>
        )}
        {gActive && (
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {requests.length.toLocaleString()} of {allRequests.length.toLocaleString()} tickets · {assets.length.toLocaleString()} of {allAssets.length.toLocaleString()} assets
          </span>
        )}
      </div>
      <div style={S.kpiGrid}>
        <KpiCard icon={Ticket}       label={id ? "Total Requests" : "Total Requests"} value={yearRequests.length} color="rgb(var(--brand-600))"
          onClick={() => goToRequests(null, null)} />
        <KpiCard icon={CheckCircle2} label={t('sdp.closedRequests')} value={closedCount}    color="#16a34a"
          onClick={() => goToRequests(r => /closed|resolved/i.test(r.status || ""), t('sdp.statusClosed'))} />
        <KpiCard icon={Ticket}       label={t('sdp.openRequests')}   value={openCount}      color="#d97706"
          onClick={() => goToRequests(r => !/closed|resolved/i.test(r.status || ""), t('sdp.statusOpen'))} />
        <KpiCard icon={HardDrive}    label={id ? "Total Assets" : "Total Assets"}       value={assets.length}  color="#7c3aed"
          onClick={() => goToAssets(null, null)} />
        <KpiCard icon={MapPin}       label={t('sdp.sites')}               value={siteCount}      color="#0891b2"
          onClick={() => goToAssets(null, null)} />
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={S.cardT}>{t('sdp.requestsStatus')}</div>
          {(() => {
            const total = reqByStatus.reduce((a, x) => a + x.value, 0);
            const pct = (v) => total ? Math.round((v / total) * 1000) / 10 : 0;
            return (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
                <div style={{ position: "relative", flex: "0 0 220px", height: 220, margin: "0 auto" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={reqByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={68} outerRadius={100} paddingAngle={reqByStatus.length > 1 ? 2 : 0} cornerRadius={4} stroke="none"
                           style={{ cursor: "pointer" }}
                           onClick={(d) => d && goToRequests(r => (r.status || "—") === d.name, `Status: ${d.name}`)}>
                        {reqByStatus.map((e, i) => <Cell key={i} fill={MC[i % MC.length]} />)}
                      </Pie>
                      <Tooltip {...TT} formatter={(v, n) => [`${v.toLocaleString()} (${pct(v)}%)`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{total.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, letterSpacing: .8, textTransform: "uppercase", marginTop: 4 }}>Total Tickets</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {reqByStatus.map((s, i) => (
                    <div key={s.name} onClick={() => goToRequests(r => (r.status || "—") === s.name, `Status: ${s.name}`)}
                         style={{ cursor: "pointer", padding: "6px 8px", borderRadius: 8 }}
                         onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: MC[i % MC.length], flexShrink: 0 }} />
                        <span style={{ flex: 1, color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                        <span style={{ fontWeight: 800, color: "#0f172a" }}>{s.value.toLocaleString()}</span>
                        <span style={{ color: "#94a3b8", width: 48, textAlign: "right" }}>{pct(s.value)}%</span>
                      </div>
                      <div style={{ height: 4, background: "#f1f5f9", borderRadius: 4, marginTop: 5, marginLeft: 18 }}>
                        <div style={{ width: `${pct(s.value)}%`, height: "100%", background: MC[i % MC.length], borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
        <div style={S.card}>
          <div style={{ ...S.cardT, justifyContent: "space-between" }}>
            <span>{t('sdp.assetsType')}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "rgb(var(--brand-700))", background: "rgb(var(--brand-50))", padding: "3px 10px", borderRadius: 999, letterSpacing: 0, textTransform: "none" }}>
              {assets.length.toLocaleString()} assets
            </span>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(260, assetByType.length * 30 + 40)}>
            <BarChart data={assetTypeState.rows} layout="vertical" margin={{ left: 10, right: 70 }} barCategoryGap={6}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: "#334155" }} axisLine={false} tickLine={false} />
              <Tooltip {...TT} cursor={{ fill: "rgba(148,163,184,.12)" }}
                       formatter={(v, n, p) => [`${v.toLocaleString()} (${Math.round(v / (p.payload.value || 1) * 100)}%)`, n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {assetTypeState.states.map((st, i) => {
                const last = i === assetTypeState.states.length - 1;
                return (
                  <Bar key={st} dataKey={st} stackId="st" fill={MC[i % MC.length]}
                       style={{ cursor: "pointer" }}
                       onClick={(d) => d && goToAssets(a => (a.state || "—") === st &&
                         (d.name === "Lainnya" ? !assetTypeState.topTypes.includes(a.product_type || "—") : (a.product_type || "—") === d.name),
                         `Type: ${d.name} · State: ${st}`)}>
                    {last && <LabelList dataKey="value" position="right" offset={8}
                               formatter={(v) => `${(v || 0).toLocaleString()} · ${assets.length ? Math.round(v / assets.length * 100) : 0}%`}
                               style={{ fontSize: 11, fontWeight: 700, fill: "#334155" }} />}
                  </Bar>
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={S.tabBar}>
        <button style={{ ...S.tab, ...(tab === "requests" ? S.tabOn : {}) }} onClick={() => { setTab("requests"); setQuickFilter(null); }}>
          {id ? "Requests" : "Requests"} ({yearRequests.length})
        </button>
        <button style={{ ...S.tab, ...(tab === "assets" ? S.tabOn : {}) }} onClick={() => { setTab("assets"); setQuickFilter(null); }}>
          {id ? "Assets" : "Assets"} ({assets.length})
        </button>
        <button style={{ ...S.tab, ...(tab === "analytics" ? S.tabOn : {}) }} onClick={() => { setTab("analytics"); setQuickFilter(null); }}>
          📊 {id ? "Analytics" : "Analytics"}
        </button>
      </div>
      {tab === "analytics" ? (
        <AnalyticsView requests={yearRequests} assets={assets} id={id} onGoToRequests={goToRequests} onGoToAssets={goToAssets} />
      ) : (
      <>
      {tab === "assets" && <AssetPivot key="pv-assets" assets={assets} onPick={goToAssets} />}
      {tab === "requests" && <AssetPivot key="pv-req" assets={yearRequests} onPick={goToRequests} dims={REQ_PIVOT_DIMS}
        init={{ row: "technician", row2: "", col: "status" }} title="Pivot Requests"
        note="Klik angka / nama baris / total kolom untuk memfilter tabel Requests di bawah. Mengikuti filter Tahun, Company, dan Ticket Status di atas." />}
      {quickFilter && (tab === "requests" || tab === "assets") && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 12, color: "rgb(var(--brand-700))", background: "rgb(var(--brand-50))", border: "1px solid #bfdbfe", borderRadius: 8, padding: "6px 12px", width: "fit-content" }}>
          {t('sdp.activeFilter')} <b>{quickFilter.label}</b>
          <button onClick={() => setQuickFilter(null)} style={{ border: "none", background: "none", cursor: "pointer", color: "rgb(var(--brand-700))", fontWeight: 700 }}>×</button>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <input style={{ ...S.input, flex: "1 1 240px" }} placeholder={t('sdp.search')} value={q} onChange={e => setQ(e.target.value)} />
        {colFCount > 0 && (
          <button style={S.btn} onClick={() => setColF({})}>✕ Clear column filters ({colFCount})</button>
        )}
        <div style={{ position: "relative" }}>
          <button style={S.btn} onClick={() => setColPickerOpen(o => !o)}>
            ▦ Columns{hiddenCount ? ` (${cols.length}/${allCols.length})` : ""}
          </button>
          {colPickerOpen && (
            <div style={{ position: "absolute", zIndex: 40, top: "110%", left: 0, minWidth: 220,
                          maxHeight: 340, overflowY: "auto", background: "#fff", padding: 10,
                          border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 24px rgba(15,23,42,.12)" }}>
              {allCols.map(c => (
                <label key={c.k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 2px", cursor: "pointer" }}>
                  <input type="checkbox" checked={!hidden[c.k]} onChange={() => toggleCol(c.k)} />
                  {c.h}
                </label>
              ))}
              <button style={{ ...S.btn, width: "100%", marginTop: 8 }}
                      onClick={() => { setHidden({}); try { localStorage.removeItem(COLS_LS_KEY); } catch (e) {} }}>
                Show all
              </button>
            </div>
          )}
        </div>
        <button style={S.btn} onClick={exportXlsx}>⬇️ Export Excel</button>
      </div>

      {isMobile ? (
        <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
          <CardList
            items={filtered.slice(0, 500).map((r, i) => ({ ...r, __i: i }))}
            keyOf={(r) => String(r.__i)}
            title={cardTitle}
            badge={cardBadge}
            subtitle={cardSubtitle}
            onOpen={(r) => (tab === "requests" ? setTicketRow(r) : tab === "assets" ? setAssetRow(r) : setDetailRow(r))}
            emptyText={loading ? (t('sdp.loading')) : (t('sdp.noData'))}
          />
          {filtered.length > 500 && (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "#64748b" }}>
              {id ? `Menampilkan 500 dari ${filtered.length.toLocaleString()} baris — gunakan pencarian atau Export Excel untuk semua.` : `Showing 500 of ${filtered.length.toLocaleString()} rows — use search or Export Excel for all.`}
            </div>
          )}
        </div>
      ) : (
      <div style={{
        ...S.card, padding: 0,
        // Tinggi dikunci ke tinggi layar: scrollbar horizontal selalu terlihat tanpa
        // harus menggulir ke dasar 500 baris dulu.
        overflow: "auto", maxHeight: "calc(100vh - 260px)",
      }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead><tr>{cols.map((c, i) => {
            const active = sort.key === c.k && sort.dir !== 0;
            const arrow = !active ? "↕" : sort.dir === 1 ? "↑" : "↓";
            return (
              <th key={c.k} style={{ ...S.th, cursor: "pointer", userSelect: "none", height: 34,
                                     color: active ? "rgb(var(--brand-600))" : S.th.color,
                                     position: "sticky", top: 0, zIndex: i === 0 ? 6 : 4,
                                     background: "#f8fafc",
                                     ...(i === 0 ? { left: 0, minWidth: 190 } : {}) }}
                  onClick={() => toggleSort(c.k)} title={t('sdp.clickSort')}>
                {c.h} <span style={{ opacity: active ? 1 : .4, fontSize: 11 }}>{arrow}</span>
              </th>
            );
          })}</tr>
          {/* Baris filter per kolom. Dropdown utk kolom bernilai sedikit, kotak ketik utk sisanya. */}
          <tr>{cols.map((c, i) => {
            const opts = colOptions[c.k];
            const cur  = colF[c.k]?.v ?? "";
            return (
              <th key={c.k} style={{ ...S.th, padding: "4px 6px", fontWeight: 400, background: "#f1f5f9",
                                     position: "sticky", top: 34, zIndex: i === 0 ? 6 : 4,
                                     ...(i === 0 ? { left: 0 } : {}) }}>
                {opts ? (
                  <select style={{ ...S.input, width: "100%", padding: "3px 4px", fontSize: 11 }}
                          value={cur} onChange={e => setColFilter(c.k, e.target.value, true)}>
                    <option value="">All</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input style={{ ...S.input, width: "100%", padding: "3px 6px", fontSize: 11 }}
                         placeholder="…" value={cur}
                         onChange={e => setColFilter(c.k, e.target.value, false)} />
                )}
              </th>
            );
          })}</tr></thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td style={S.td} colSpan={cols.length}>{loading ? (t('sdp.loading')) : (t('sdp.noData'))}</td></tr>
              : filtered.slice(0, 500).map((r, i) => (
                <tr key={i} className="sdp-tr" onClick={() => (tab === "requests" ? setTicketRow(r) : tab === "assets" ? setAssetRow(r) : null)} style={{ cursor: "pointer" }} title={tab === "requests" ? "Click to view ticket details" : "Click to view asset details"}>{cols.map((c, ci) => {
                  const v = r[c.k];
                  const isPill = (c.k === "status" || c.k === "state") && v;
                  const pal = { emerald: ["#ecfdf5", "#047857"], amber: ["#fffbeb", "#b45309"], rose: ["#fff1f2", "#be123c"], blue: ["#eff6ff", "#1d4ed8"] }[statusTone(v || "")];
                  const overdue = tab === "requests" && c.k === "due_by_time" && r.is_overdue;
                  return (
                  <td key={c.k} style={ci === 0
                    ? { ...S.td, position: "sticky", left: 0, zIndex: 2, background: "#fff", fontWeight: 700, color: "rgb(var(--brand-700))" }
                    : { ...S.td, ...(overdue ? { color: "#dc2626", fontWeight: 600 } : {}) }}>
                    {isPill
                      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: pal[0], color: pal[1], whiteSpace: "nowrap" }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: pal[1] }} />{v}
                        </span>
                      : (ci === 0 && tab === "requests" && v ? `#${v}` : (v ?? "—"))}
                  </td>);
                })}</tr>
              ))}
          </tbody>
        </table>
        {filtered.length > 500 && (
          <div style={{ padding: "8px 12px", fontSize: 12, color: "#64748b" }}>
            {id ? `Menampilkan 500 dari ${filtered.length.toLocaleString()} baris — gunakan pencarian atau Export Excel untuk semua.` : `Showing 500 of ${filtered.length.toLocaleString()} rows — use search or Export Excel for all.`}
          </div>
        )}
      </div>
      )}

      {ticketRow && <TicketDetailDrawer row={ticketRow} onClose={() => setTicketRow(null)} />}
      {assetRow && <AssetDetailDrawer row={assetRow} onClose={() => setAssetRow(null)} onOpenTicket={(t) => { setAssetRow(null); setTicketRow(t); }} />}
      {isMobile && detailRow && (
        <DetailSheet
          open
          title={tab === "requests" ? `#${detailRow.display_id}` : (detailRow.name || "—")}
          badge={(() => {
            const b = cardBadge(detailRow);
            if (!b) return null;
            const toneCss = { emerald: "#ecfdf5;#059669", amber: "#fffbeb;#b45309", rose: "#fff1f2;#e11d48", blue: "rgb(var(--brand-50));rgb(var(--brand-600))" }[b.tone] || "#f1f5f9;#64748b";
            const [bg, fg] = toneCss.split(";");
            return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: bg, color: fg }}>{b.label}</span>;
          })()}
          fields={cols.map((c) => ({ label: c.h, value: detailRow[c.k] }))}
          onClose={() => setDetailRow(null)}
        />
      )}
      </>
      )}
    </div>
  );
}
